import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import "./App.css";
import "./assets/css/container/login/LoginMain.css";
import "./assets/css/global.css";

import LoginMain from "./container/login/LoginMain";
import SocialLoginButtons from "./component/SocialLoginButtons";
import NaverCallback from "./component/NaverCallback";

import MainLayout from "./container/main/MainLayout";
import MyInfo from "./container/main/MyInfo";
import MyFact from "./component/factList/MyFact";
import WriteNew from "./container/board/WriteNew";
import ViewPost from "./container/board/ViewPost";
import MainWrapper from "./container/main/MainWrapper";
import InterestFact from "./component/factList/InterestFact";
import UpdatePost from "./container/board/UpdatePost";
import WriteRep from "./container/board/WriteRep";
import { AuthProvider, useAuth } from "./global/AuthContext";
import PrivacyConsentPage from "./container/login/PrivacyConsentPage";
import AboutFactWeb from "./container/main/AboutFactWeb";
import NoticeList from "./container/board/boardClasses/NoticeList";
import FreeBoardList from "./container/board/freeboard/FreeBoardList";
import ViewFreePost from "./container/board/freeboard/ViewFreePost";
import FreePostEditor from "./container/board/freeboard/FreePostEditor";
import ViewNotice from "./container/board/ViewNotice";
import WriteNotice from "./container/board/WriteNotice";
import UpdateNotice from "./container/board/UpdateNotice";

import TermsPolicy from "./container/main/footerClasses/TermsPolicy";
import PrivacyPolicy from "./container/main/footerClasses/PrivacyPolicy";
import OperationPolicy from "./container/main/footerClasses/OperationPolicy";
import VerificationPolicy from "./container/main/footerClasses/VerificationPolicy";


import SearchedFactWrapper from "./container/wrapper/SearchedFactWrapper";
import MyStatsPage from "./container/main/MyStatsPage";
import {ExpTableProvider} from "./global/ExpTableContext";

// 🔐 로그인이 필요한 페이지를 보호하는 컴포넌트
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
	const { user, loading } = useAuth();

	// AuthContext 초기화 대기
	if (loading) {
		return <div className="route-loading"></div>;
	}

	// 로그인 안 됐으면 차단
	if (!user) {
		return <Navigate to="/login" replace />;
	}

	return children;
};

function AppInner() {
	// 🔑 토큰 여부로 로그인 상태 관리
	const [isLoggedIn, setIsLoggedIn] = useState<boolean>(!!localStorage.getItem("accessToken"));
	const navigate = useNavigate();
	const { refreshUser } = useAuth(); // ✅ AuthContext에서 불러오기

	useEffect(() => {
		// localStorage 토큰 변경 감지 → 로그인 상태 갱신
		const handleStorageChange = () => {
			setIsLoggedIn(!!localStorage.getItem("accessToken"));
		};
		window.addEventListener("storage", handleStorageChange);
		return () => window.removeEventListener("storage", handleStorageChange);
	}, []);

	const handleLoginSuccess = async (token: string) => {
		console.log("로그인 성공:", token);
		localStorage.setItem("accessToken", token); // 토큰 저장
		setIsLoggedIn(true);

		// 커스텀 이벤트 발생시켜서 다른 컴포넌트에 알림
		window.dispatchEvent(new Event("loginStateChanged"));

		await refreshUser(); // ✅ 로그인 직후 사용자 정보 갱신
		navigate("/", { replace: true });
	};

	return (
		<Routes>
			{/* 개인정보 동의 페이지 */}
			<Route path="/privacy-consent" element={<PrivacyConsentPage />} />

			{/* 로그인 페이지 (독립적으로) */}
			<Route
				path="/login"
				element={
					<LoginMain>
						<SocialLoginButtons onLoginSuccess={handleLoginSuccess} />
					</LoginMain>
				}
			/>
			
			{/* 네이버 콜백 */}
			<Route
				path="/naver-callback"
				element={<NaverCallback onLoginSuccess={handleLoginSuccess} />}
			/>

			{/* 메인 레이아웃 (로그인 여부 무관하게 접근 가능) */}
			<Route element={<MainLayout />}>
				{/* 메인 페이지 - 로그인 없이도 접근 가능 */}
				<Route path="/" element={<MainWrapper />} />
				
				{/* 게시글 보기 - 로그인 없이도 접근 가능 */}
				<Route path="/viewPost/:postId" element={<ViewPost />} />
				
				{/* 팩트웹 소개 - 로그인 없이도 접근 가능 */}
				<Route path="/about" element={<AboutFactWeb />} />
				
				{/* 공지사항 목록 - 로그인 없이도 접근 가능 */}
				<Route path="/notices" element={<NoticeList />} />
                <Route path="/freeboard" element={<FreeBoardList />} />
                <Route path="/freeboard/:id" element={<ViewFreePost />} />
                <Route path="/freeboard/write" element={<ProtectedRoute><FreePostEditor /></ProtectedRoute>} />
                <Route path="/freeboard/update/:id" element={<ProtectedRoute><FreePostEditor /></ProtectedRoute>} />
				
				{/* 공지사항 보기 - 로그인 없이도 접근 가능 */}
				<Route path="/notices/:id" element={<ViewNotice />} />
				
				{/* 검색 결과 - 로그인 없이도 접근 가능 */}
				<Route path="/search/:type" element={<SearchedFactWrapper />} />

				{/* ✅ 정책 페이지 (로그인 불필요) */}
				<Route path="/policy/terms" element={<TermsPolicy />} />
				<Route path="/policy/privacy" element={<PrivacyPolicy />} />
				<Route path="/policy/operation" element={<OperationPolicy />} />
				<Route path="/policy/verification" element={<VerificationPolicy />} />

				{/* 🔐 로그인이 필요한 페이지들 */}
				<Route path="/myinfo" element={<ProtectedRoute><MyInfo /></ProtectedRoute>} />
				<Route path="/writeNew" element={<ProtectedRoute><WriteNew /></ProtectedRoute>} />
				<Route path="/updatePost/:postId" element={<ProtectedRoute><UpdatePost /></ProtectedRoute>} />
				<Route path="/writeRep/:parentId" element={<ProtectedRoute><WriteRep /></ProtectedRoute>} />
				{/*<Route path="/myfact" element={<ProtectedRoute><MyFact /></ProtectedRoute>} />*/}
				{/*<Route path="/interestfact" element={<ProtectedRoute><InterestFact /></ProtectedRoute>} />*/}
				<Route path="/stats" element={<ProtectedRoute><MyStatsPage /></ProtectedRoute>} />
				<Route path="/notices/write" element={<ProtectedRoute><WriteNotice /></ProtectedRoute>} />
				<Route path="/notices/update/:id" element={<ProtectedRoute><UpdateNotice /></ProtectedRoute>} />
			</Route>

			{/* 404 페이지 */}
			<Route path="*" element={<div>404 Not Found</div>} />
		</Routes>
	);
}

function App() {
	return (
		<AuthProvider>
			<ExpTableProvider>
				<AppInner />
			</ExpTableProvider>
		</AuthProvider>
	);
}

export default App;
